import { useEffect, useRef, useState } from "react";
import Image from "@/atoms/Image";
import H2 from "@/atoms/text/H2";
import H3 from "@/atoms/text/H3";
import Para from "@/atoms/text/Para";
import { isMobileScreen } from "@/utils/functions";
import { RoundedTickIcon } from "@/atoms/Icons";

import { Pagination } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";

export default function Slider({
  data,
  variant = "services",
  title,
  subTitle,
  bgColor = "white",
}) {
  const [isMobile, setIsMobile] = useState(null);
  let imgObj;
  if (variant === "services") {
    imgObj = {
      fileName: "female-agent-chatting",
      altName: "Female answering agent chatting with client",
      isAlignBottom: false,
      desktopWidth: "500",
    };
  }
  if (variant === "solutions") {
    imgObj = {
      fileName: data[0].image.fileName,
      altName: data[0].image.altName,
      isAlignBottom: false,
      desktopWidth: "500",
    };
  }

  const [imageName, setImage] = useState(imgObj);
  const [isSliderStarted, setIsSliderStarted] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const autoplayTimer = useRef(null);
  const sliderRef = useRef(null);

  const showContentHandler = (image, idx) => {
    setActiveIndex(idx);
    setImage(image);
    resetAutoplayTimer();
  };

  const resetAutoplayTimer = () => {
    clearInterval(autoplayTimer.current);
    autoplayTimer.current = setInterval(() => {
      setActiveIndex((prevIndex) => {
        const nextIndex = (prevIndex + 1) % data.length;
        setImage(data[nextIndex].image);
        return nextIndex;
      });
    }, 7000);
  };

  useEffect(() => {
    setIsMobile(isMobileScreen);
    isSliderStarted
      ? resetAutoplayTimer()
      : window.addEventListener("scroll", playSlider);

    return () => {
      clearInterval(autoplayTimer.current);
      window.removeEventListener("scroll", playSlider);
    };
  }, [imageName, isMobileScreen]);

  const playSlider = () => {
    const currentSliderRef = sliderRef.current;
    if (currentSliderRef) {
      const rect = currentSliderRef.getBoundingClientRect();

      if (window.innerHeight * 0.5 > rect.top) {
        if (autoplayTimer.current === null) {
          setIsSliderStarted(true);
          resetAutoplayTimer();
        }
      }
    }
  };

  const pagination = {
    clickable: true,
    renderBullet: function (index, className) {
      return '<span class="' + className + '">' + "</span>";
    },
  };

  return variant === "services" ? (
    <section className={`bc-${bgColor} pv-15-d pt-8 pb-10`} id="services">
      <div className="container ps-r">
        <div className="wrapper ta-center-d mb-15-d mb-7 m-center">
          <H2
            className="bold mb-5"
            dangerouslySetInnerHTML={{ __html: title }}
          />
          <Para
            desktopSize="lg"
            mobileSize="lg"
            dangerouslySetInnerHTML={{ __html: subTitle }}
          />
          <style jsx>{`
            .wrapper {
              max-width: 865px;
            }
          `}</style>
        </div>
        {isMobile ? (
          <Swiper
            pagination={pagination}
            modules={[Pagination]}
            breakpoints={{
              1024: {
                slidesPerView: 1,
                loop: true,
                autoplay: {
                  delay: 3000,
                },
              },
            }}
            className="industries-swiper"
          >
            {data.map(({ title, content, image }, idx) => {
              return (
                <SwiperSlide key={idx}>
                  <H3 desktopSize="xs" mobileSize="xs" className="bold mb-3">
                    {`${idx + 1}. ${title}`}
                  </H3>
                  <Para
                    className="slider__content--text"
                    desktopSize="lg"
                    mobileSize="lg"
                    dangerouslySetInnerHTML={{ __html: content }}
                  />
                  <Image
                    fileName={image.fileName}
                    fileFormat="png"
                    desktopWidth={image.desktopWidth}
                    mobileWidth="335"
                    className="slider-image mt-7"
                    alt={image.altName}
                  />
                </SwiperSlide>
              );
            })}
          </Swiper>
        ) : (
          <div className="slider-wrapper fx fx--sb">
            <ul className="slider__contents w-50-d">
              {data.map(({ title, content, image }, idx) => (
                <li
                  key={idx}
                  className={`slider__content ${
                    idx === activeIndex ? "active" : ""
                  } ${idx === data.length - 1 ? "" : "mb-8"}`}
                  onClick={() => showContentHandler(image, idx)}
                >
                  <H3 desktopSize="xs" mobileSize="xs" className="bold mb-3">
                    {title}
                  </H3>
                  <Para
                    className="slider__content--text"
                    desktopSize="lg"
                    mobileSize="lg"
                    dangerouslySetInnerHTML={{ __html: content }}
                  />
                </li>
              ))}
            </ul>
            <div className="slider-image w-50-d">
              {data.map(({ image }, idx) => (
                <Image
                  key={idx}
                  fileName={image.fileName}
                  fileFormat="png"
                  desktopWidth={image.desktopWidth}
                  className={`ps-r ${
                    image.isAlignBottom ? "align-bottom" : ""
                  } ${idx === activeIndex ? "active animate" : ""} `}
                  alt={image.altName}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  ) : (
    variant === "solutions" && (
      <section
        ref={sliderRef}
        className={`solutions-slider bc-${bgColor} pv-15-d pt-8`}
        id="services"
      >
        <div className="container ps-r">
          <div className="wrapper ta-center-d mb-8-d m-center">
            <H2
              className="bold mb-5"
              dangerouslySetInnerHTML={{ __html: title }}
            />
            <Para
              desktopSize="lg"
              mobileSize="lg"
              dangerouslySetInnerHTML={{ __html: subTitle }}
            />
          </div>
        </div>
        {isMobile ? (
          <div>
            {data.map(
              (
                { title, content, list, image, hasCta, cta, hasMobileAppImg },
                idx
              ) => {
                return (
                  <div
                    className={`${
                      idx === 1
                        ? bgColor === "white"
                          ? "bc-blue-light"
                          : "bc-white"
                        : bgColor === "white"
                        ? "bc-white"
                        : "bc-blue-light"
                    } ${idx === 0 ? "pt-7" : "pt-8"} ${
                      hasMobileAppImg ? "" : "pb-8"
                    }`}
                    key={idx}
                  >
                    <div className="container ps-r">
                      <H3
                        desktopSize="xs"
                        mobileSize="xs"
                        className="bold mb-3"
                      >
                        {title}
                      </H3>
                      <Para
                        className="slider__content--text mb-5"
                        desktopSize="lg"
                        mobileSize="lg"
                        dangerouslySetInnerHTML={{ __html: content }}
                      ></Para>
                      <ul className="mb-7">
                        {list &&
                          list.map((listItem, idx) => (
                            <li key={idx} className="mb-3">
                              {typeof listItem === "object" && listItem.href ? (
                                <a
                                  href={listItem.href}
                                  className="text text--lg"
                                >
                                  <RoundedTickIcon className="mr-2" />
                                  {listItem.text}
                                </a>
                              ) : (
                                <Para desktopSize="lg" mobileSize="lg">
                                  <RoundedTickIcon className="mr-2" />
                                  {listItem}
                                </Para>
                              )}
                            </li>
                          ))}
                      </ul>
                      {hasCta && (
                        <a
                          href={cta.href}
                          className="btn btn--link text--md btn--link-lg"
                        >
                          {cta.text}
                        </a>
                      )}
                      <Image
                        fileName={image.fileName}
                        fileFormat="png"
                        desktopWidth={image.desktopWidth}
                        mobileWidth="335"
                        mobileHeight={image.mobileHeight}
                        alt={image.altName}
                        className={`mt-7 ${
                          image.isAlignBottom ? "align-bottom" : ""
                        }`}
                      />
                    </div>
                  </div>
                );
              }
            )}
          </div>
        ) : (
          <div className="container ps-r">
            <div>
              <ul className="slider__contents fx-d fx--sb-d m-center mb-10-d">
                {data.map(({ title, image }, idx) => (
                  <li
                    key={idx}
                    className={`slider__title ta-center ${
                      idx === activeIndex ? "active" : ""
                    }`}
                    onClick={() => showContentHandler(image, idx)}
                  >
                    <H3
                      desktopSize="xs"
                      mobileSize="xs"
                      className="text--lg fw-semibold mb-2"
                    >
                      {title}
                    </H3>
                  </li>
                ))}
              </ul>
              <div className="slider-wrapper fx fx--sb fx--cv">
                <div className="slider-image w-50-d">
                  {data.map(({ image }, idx) => (
                    <Image
                      key={idx}
                      fileName={image.fileName}
                      fileFormat="png"
                      desktopWidth={image.desktopWidth}
                      className={`${idx === activeIndex ? "active" : ""} ${
                        image.isAlignBottom ? "align-bottom" : ""
                      }`}
                      alt={image.altName}
                    />
                  ))}
                </div>
                <ul className="slider__contents w-50-d">
                  {data.map(({ content, list, hasCta, cta }, idx) => (
                    <li
                      key={idx}
                      className={`slider__content ${
                        idx === activeIndex ? "active" : ""
                      }`}
                    >
                      <article className={`slider__content--text  `}>
                        <Para
                          className="mb-5"
                          desktopSize="lg"
                          mobileSize="lg"
                          dangerouslySetInnerHTML={{ __html: content }}
                        ></Para>
                        <ul className="mb-7">
                          {list &&
                            list.map((listItem, idx) => (
                              <li key={idx} className="mb-3">
                                {typeof listItem === "object" &&
                                listItem.href ? (
                                  <a
                                    href={listItem.href}
                                    className="text text--lg"
                                  >
                                    <RoundedTickIcon className="mr-2" />
                                    {listItem.text}
                                  </a>
                                ) : (
                                  <Para desktopSize="lg" mobileSize="lg">
                                    <RoundedTickIcon className="mr-2" />
                                    {listItem}
                                  </Para>
                                )}
                              </li>
                            ))}
                        </ul>
                        {hasCta && (
                          <a
                            href={cta.href}
                            className="btn btn--link text--md btn--link-lg"
                          >
                            {cta.text}
                          </a>
                        )}
                      </article>
                    </li>
                  ))}
                </ul>

                <style jsx>{`
                  .slider-wrapper {
                    min-height: 378px;
                  }
                  .slider-image figure {
                    position: absolute;
                    left: 0;
                  }
                `}</style>
              </div>
            </div>
          </div>
        )}
      </section>
    )
  );
}
